import React from 'react';

export interface MatchListRowMatch {
  id: string;
  name: string;
  date: string;
  time: string;
  tournament: { id: string; name: string; logo: string };
  homeTeam: { name: string; logo: string; score: number };
  awayTeam: { name: string; logo: string; score: number };
  homePenaltyScore?: number;
  awayPenaltyScore?: number;
  status: 'upcoming' | 'live' | 'finished';
}

interface MatchListRowProps {
  match: MatchListRowMatch;
  onMatchClick: (matchId: string) => void;
  /** Optional slot for app-specific actions (e.g. publish badge, report link) */
  rightContent?: React.ReactNode;
  className?: string;
}

/**
 * Compact list row for match listings. Used when view mode is "list".
 */
const MatchListRow: React.FC<MatchListRowProps> = ({
  match,
  onMatchClick,
  rightContent,
  className = '',
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onMatchClick(match.id)}
    onKeyDown={(e) => e.key === 'Enter' && onMatchClick(match.id)}
    className={`flex flex-wrap items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-3 bg-white border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer text-left ${className}`}
  >
    <div className="flex-1 min-w-0 flex items-center gap-2 sm:gap-3 flex-wrap">
      <img
        src={match.tournament.logo}
        alt=""
        className="w-5 h-5 rounded object-cover flex-shrink-0"
        onError={(e) => {
          e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=⚽';
        }}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate" title={match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}>
          {match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}
        </p>
        <p className="text-xs text-gray-500 truncate">{match.tournament.name}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs text-gray-500">{match.date}</span>
        <span className="text-xs text-gray-500">{match.time}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
        <span className="text-sm text-gray-900 truncate max-w-16 sm:max-w-20">{match.homeTeam.name}</span>
        {match.status === 'upcoming' ? (
          <span className="text-xs text-gray-400">vs</span>
        ) : (
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
            {match.homeTeam.score}
            {match.homePenaltyScore !== undefined && match.awayPenaltyScore !== undefined &&
             (match.homePenaltyScore > 0 || match.awayPenaltyScore > 0) && (
              <span className="text-gray-500 font-normal"> ({match.homePenaltyScore})</span>
            )}
            {' - '}
            {match.awayTeam.score}
            {match.homePenaltyScore !== undefined && match.awayPenaltyScore !== undefined &&
             (match.homePenaltyScore > 0 || match.awayPenaltyScore > 0) && (
              <span className="text-gray-500 font-normal"> ({match.awayPenaltyScore})</span>
            )}
          </span>
        )}
        <span className="text-sm text-gray-900 truncate max-w-16 sm:max-w-20">{match.awayTeam.name}</span>
      </div>
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
          match.status === 'live'
            ? 'bg-red-100 text-red-700'
            : match.status === 'upcoming'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-gray-100 text-gray-700'
        }`}
      >
        {match.status === 'live' ? 'LIVE' : match.status === 'upcoming' ? 'UPCOMING' : 'COMPLETED'}
      </span>
    </div>
    {rightContent != null && <div className="flex-shrink-0">{rightContent}</div>}
  </div>
);

export default MatchListRow;
